import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============================================================================
// STORAGE KEYS AND DURATIONS
// ============================================================================
const TOKEN_KEY = 'llgw_auth_token';
const TOKEN_EXPIRY_KEY = 'llgw_auth_token_expiry';
const USER_KEY = 'llgw_auth_user';
const REMEMBER_KEY = 'llgw_auth_remember';
const SESSION_ID_KEY = 'llgw_session_id';

// Token durations
const TOKEN_DURATION_DEFAULT = 24 * 60 * 60 * 1000; // 1 day
const TOKEN_DURATION_REMEMBER = 30 * 24 * 60 * 60 * 1000; // 30 days

// ============================================================================
// STORAGE UTILITIES - Robust localStorage handling
// ============================================================================

/**
 * Generate a unique session ID for cross-tab synchronization
 */
const generateSessionId = () => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Safe localStorage get with error handling
 */
const safeGetItem = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    console.warn(`Failed to read ${key} from localStorage:`, e);
    return null;
  }
};

/**
 * Safe localStorage set with error handling
 */
const safeSetItem = (key, value) => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (e) {
    console.warn(`Failed to write ${key} to localStorage:`, e);
    return false;
  }
};

/**
 * Safe localStorage remove with error handling
 */
const safeRemoveItem = (key) => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (e) {
    console.warn(`Failed to remove ${key} from localStorage:`, e);
    return false;
  }
};

/**
 * Get stored token if not expired
 */
const getStoredToken = () => {
  try {
    const token = safeGetItem(TOKEN_KEY);
    const expiryStr = safeGetItem(TOKEN_EXPIRY_KEY);
    
    if (!token || !expiryStr) {
      return null;
    }
    
    const expiry = parseInt(expiryStr, 10);
    const now = Date.now();
    
    // Check if token is expired
    if (now >= expiry) {
      console.log('Auth token expired, clearing...');
      clearStoredAuth();
      return null;
    }
    
    // If token expires within 1 hour, we might want to refresh it (future enhancement)
    const oneHour = 60 * 60 * 1000;
    if (expiry - now < oneHour) {
      console.log('Auth token expiring soon');
    }
    
    return token;
  } catch (e) {
    console.warn('Error getting stored token:', e);
    return null;
  }
};

/**
 * Get stored user data
 */
const getStoredUser = () => {
  try {
    const userStr = safeGetItem(USER_KEY);
    if (!userStr) return null;
    return JSON.parse(userStr);
  } catch (e) {
    console.warn('Error parsing stored user:', e);
    return null;
  }
};

/**
 * Get remember preference
 */
const getRememberPreference = () => {
  const pref = safeGetItem(REMEMBER_KEY);
  return pref !== 'false'; // Default to true if not set
};

/**
 * Store authentication data
 */
const setStoredAuth = (token, user, rememberMe = true) => {
  const duration = rememberMe ? TOKEN_DURATION_REMEMBER : TOKEN_DURATION_DEFAULT;
  const expiry = Date.now() + duration;
  
  safeSetItem(TOKEN_KEY, token);
  safeSetItem(TOKEN_EXPIRY_KEY, expiry.toString());
  safeSetItem(REMEMBER_KEY, rememberMe ? 'true' : 'false');
  
  if (user) {
    safeSetItem(USER_KEY, JSON.stringify(user));
  }
  
  // Generate session ID for cross-tab sync
  if (!safeGetItem(SESSION_ID_KEY)) {
    safeSetItem(SESSION_ID_KEY, generateSessionId());
  }
  
  console.log(`Auth stored, expires: ${new Date(expiry).toLocaleString()}, remember: ${rememberMe}`);
};

/**
 * Update stored user data only
 */
const setStoredUser = (user) => {
  if (user) {
    safeSetItem(USER_KEY, JSON.stringify(user));
  } else {
    safeRemoveItem(USER_KEY);
  }
};

/**
 * Clear all authentication data
 */
const clearStoredAuth = () => {
  safeRemoveItem(TOKEN_KEY);
  safeRemoveItem(TOKEN_EXPIRY_KEY);
  safeRemoveItem(USER_KEY);
  safeRemoveItem(SESSION_ID_KEY);
  // Keep REMEMBER_KEY for convenience on next login
};

/**
 * Configure axios with auth token
 */
const configureAxiosAuth = (token) => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
};

// ============================================================================
// AUTH PROVIDER COMPONENT
// ============================================================================

export const AuthProvider = ({ children }) => {
  // Initialize from localStorage immediately to prevent flash
  const [token, setToken] = useState(() => getStoredToken());
  const [user, setUser] = useState(() => getStoredUser());
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  
  // Track if we're currently fetching to prevent duplicate requests
  const fetchingRef = useRef(false);
  const mountedRef = useRef(true);

  /**
   * Fetch current user from API
   */
  const fetchUser = useCallback(async (forceRefresh = false) => {
    // Prevent duplicate fetches
    if (fetchingRef.current && !forceRefresh) {
      return;
    }
    
    const currentToken = getStoredToken();
    if (!currentToken) {
      setUser(null);
      setToken(null);
      setLoading(false);
      setInitialized(true);
      return;
    }

    fetchingRef.current = true;
    configureAxiosAuth(currentToken);

    try {
      const response = await axios.get(`${API}/auth/me`);
      
      if (mountedRef.current) {
        setUser(response.data);
        setStoredUser(response.data);
        setToken(currentToken);
      }
    } catch (error) {
      const status = error?.response?.status;
      
      if (mountedRef.current) {
        if (status === 401 || status === 403) {
          // Token is invalid - clear everything
          console.log('Auth token invalid (401/403), clearing...');
          clearStoredAuth();
          configureAxiosAuth(null);
          setToken(null);
          setUser(null);
        } else {
          // Network error or server issue - keep cached data
          console.log('Auth fetch failed (non-auth error), keeping cached data');
          const cachedUser = getStoredUser();
          if (cachedUser) {
            setUser(cachedUser);
            setToken(currentToken);
          }
        }
      }
    } finally {
      if (mountedRef.current) {
        fetchingRef.current = false;
        setLoading(false);
        setInitialized(true);
      }
    }
  }, []);

  /**
   * Initialize auth on mount
   */
  useEffect(() => {
    mountedRef.current = true;
    
    const currentToken = getStoredToken();
    if (currentToken) {
      // Set axios header immediately
      configureAxiosAuth(currentToken);
      // Fetch fresh user data
      fetchUser();
    } else {
      setLoading(false);
      setInitialized(true);
    }

    return () => {
      mountedRef.current = false;
    };
  }, [fetchUser]);

  /**
   * Handle visibility change (user returns from Stripe or another tab)
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('Page became visible, checking auth state...');
        
        // Re-check localStorage (might have changed in another tab)
        const currentToken = getStoredToken();
        const currentUser = getStoredUser();
        
        if (currentToken) {
          // Restore auth state from localStorage
          configureAxiosAuth(currentToken);
          setToken(currentToken);
          
          if (currentUser) {
            setUser(currentUser);
          }
          
          // Optionally refresh user data in background
          // fetchUser(true);
        } else if (token) {
          // Token was in state but not in localStorage (cleared in another tab)
          setToken(null);
          setUser(null);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [token]);

  /**
   * Handle storage changes from other tabs
   */
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === TOKEN_KEY) {
        console.log('Auth token changed in another tab');
        
        if (e.newValue) {
          // Token was set in another tab
          const newToken = e.newValue;
          configureAxiosAuth(newToken);
          setToken(newToken);
          
          const cachedUser = getStoredUser();
          if (cachedUser) {
            setUser(cachedUser);
          }
        } else {
          // Token was cleared in another tab (logout)
          configureAxiosAuth(null);
          setToken(null);
          setUser(null);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  /**
   * Handle page focus (similar to visibility but more aggressive)
   */
  useEffect(() => {
    const handleFocus = () => {
      // Quick check of auth state when window gains focus
      const currentToken = getStoredToken();
      const currentUser = getStoredUser();
      
      if (currentToken && !token) {
        // Token exists in storage but not in state - restore it
        console.log('Restoring auth state on focus');
        configureAxiosAuth(currentToken);
        setToken(currentToken);
        if (currentUser) {
          setUser(currentUser);
        }
      } else if (!currentToken && token) {
        // Token was cleared while window was blurred
        console.log('Auth cleared while unfocused');
        setToken(null);
        setUser(null);
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [token]);

  /**
   * Login function
   */
  const login = useCallback(async (email, password, rememberMe = true) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    
    setStoredAuth(newToken, userData, rememberMe);
    configureAxiosAuth(newToken);
    setToken(newToken);
    setUser(userData);
    
    return userData;
  }, []);

  /**
   * Register function
   */
  const register = useCallback(async (firstName, lastName, email, password) => {
    const response = await axios.post(`${API}/auth/register`, {
      first_name: firstName,
      last_name: lastName,
      email,
      password
    });
    const { token: newToken, user: userData } = response.data;
    
    setStoredAuth(newToken, userData, true);
    configureAxiosAuth(newToken);
    setToken(newToken);
    setUser(userData);
    
    return userData;
  }, []);

  /**
   * Research register function
   */
  const researchRegister = useCallback(async (firstName, lastName, email, password, acknowledgments) => {
    const response = await axios.post(`${API}/research/register`, {
      first_name: firstName,
      last_name: lastName,
      email,
      password,
      acknowledgment: acknowledgments
    });
    const { token: newToken, user: userData } = response.data;
    
    setStoredAuth(newToken, userData, true);
    configureAxiosAuth(newToken);
    setToken(newToken);
    setUser(userData);
    
    return userData;
  }, []);

  /**
   * Grant research access
   */
  const grantResearchAccess = useCallback(async (acknowledgments) => {
    const response = await axios.post(`${API}/research/grant-access`, acknowledgments);
    await fetchUser(true);
    return response.data;
  }, [fetchUser]);

  /**
   * Logout function
   */
  const logout = useCallback(() => {
    clearStoredAuth();
    configureAxiosAuth(null);
    setToken(null);
    setUser(null);
  }, []);

  /**
   * Refresh auth state (useful after external redirects)
   */
  const refreshAuth = useCallback(() => {
    const currentToken = getStoredToken();
    const currentUser = getStoredUser();
    
    if (currentToken) {
      configureAxiosAuth(currentToken);
      setToken(currentToken);
      if (currentUser) {
        setUser(currentUser);
      }
      return true;
    }
    return false;
  }, []);

  // Memoize context value
  const contextValue = useMemo(() => ({
    user,
    token,
    loading,
    initialized,
    isAuthenticated: !!user && !!token,
    isAdmin: user?.is_admin || false,
    hasResearchAccess: user?.research_access || false,
    login,
    register,
    researchRegister,
    grantResearchAccess,
    logout,
    refreshAuth,
    fetchUser
  }), [user, token, loading, initialized, login, register, researchRegister, grantResearchAccess, logout, refreshAuth, fetchUser]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
